import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { categories } from "@/mocks/categories";
import { formatHot, getCategoryById, getHotEntries } from "@/hooks/useSiteData";
import { useSEO, SITE_ORIGIN } from "@/hooks/useSEO";

export default function RankingPage() {
  const [active, setActive] = useState("all");
  const all = getHotEntries();

  const list = useMemo(
    () => (active === "all" ? all : all.filter((item) => item.categoryId === active)),
    [active, all],
  );

  useSEO({
    title: "热度榜单 - 黑料百科人物词条排行",
    description:
      "黑料百科热度榜单实时汇总全站人物词条热度排行，支持按明星风波、网红观察、影视争议等分类筛选，快速找到当前讨论最集中的话题档案。",
    keywords: "热度榜单,词条排行,明星词条,网红词条,艺人档案,黑料百科",
    path: "/ranking",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "黑料百科热度榜单",
        description: "按热度排序的人物词条排行榜，支持分类筛选。",
        url: `${SITE_ORIGIN}/ranking`,
        numberOfItems: list.length,
        itemListElement: list.map((entry, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: entry.title,
          url: `${SITE_ORIGIN}/entry/${entry.id}`,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
          { "@type": "ListItem", position: 2, name: "热度榜单", item: `${SITE_ORIGIN}/ranking` },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100/60 px-4 py-4 md:px-6">
        <Breadcrumb items={[{ label: "热度榜单" }]} />
      </div>

      <section className="border-b border-background-200 bg-background-50 px-4 py-10 md:px-6 md:py-14">
        <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-accent-700">
          <i className="ri-fire-line text-2xl"></i>
        </span>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          热度榜单
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
          每天更新一次，热度综合词条的浏览量、收藏量与近期更新频率。用下方标签可以只看某一类话题。
        </p>

        <nav aria-label="榜单分类筛选" className="mt-8 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setActive("all")}
            className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
              active === "all"
                ? "bg-primary-500 text-background-50"
                : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
            }`}
          >
            全部分类
          </button>
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActive(cat.id)}
              className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                active === cat.id
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              <i className={cat.icon}></i>
              {cat.name}
            </button>
          ))}
        </nav>
      </section>

      <div className="grid grid-cols-1 gap-8 px-4 py-10 md:px-6 md:py-12 lg:grid-cols-[minmax(0,1fr)_320px]">
        <ol className="divide-y divide-background-200 overflow-hidden rounded-lg border border-background-200 bg-background-50">
          {list.map((entry, index) => {
            const category = getCategoryById(entry.categoryId);
            return (
              <li key={entry.id}>
                <Link
                  to={`/entry/${entry.id}`}
                  className="group flex items-center gap-4 px-4 py-4 transition-colors hover:bg-primary-50/60 md:px-5"
                >
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md font-heading text-sm font-bold ${
                      index < 3 ? "bg-accent-500 text-background-50" : "bg-background-200 text-foreground-700"
                    }`}
                  >
                    {index + 1}
                  </span>
                  <img
                    src={entry.avatar}
                    alt={`${entry.name} 头像`}
                    title={`${entry.name} 词条`}
                    className="hidden h-11 w-11 shrink-0 rounded-full object-cover sm:block"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                      {entry.title}
                    </span>
                    <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
                      <span className="rounded bg-secondary-100 px-2 py-0.5 text-secondary-800">
                        {category?.name}
                      </span>
                      <span>{entry.status}</span>
                      <span>更新 {entry.updatedAt}</span>
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1 text-sm font-medium text-primary-700">
                    <i className="ri-fire-line"></i>
                    {formatHot(entry.hot)}
                  </span>
                </Link>
              </li>
            );
          })}
        </ol>

        <aside className="flex flex-col gap-6">
          <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">榜单说明</h2>
            <ul className="mt-4 space-y-3 text-sm text-foreground-600">
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-primary-500"></i>
                热度每天 09:00 更新一次，仅统计本站数据。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-primary-500"></i>
                已平息的词条会逐步下调，新话题自动进入。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-primary-500"></i>
                榜单不区分正面或负面，只反映讨论集中度。
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">按分类浏览</h2>
            <ul className="mt-4 space-y-2.5">
              {categories.map((cat) => (
                <li key={cat.id}>
                  <Link
                    to={`/category/${cat.id}`}
                    className="flex items-center gap-2 text-sm text-foreground-700 transition-colors hover:text-primary-700"
                  >
                    <i className={`${cat.icon} text-accent-600`}></i>
                    {cat.name}
                    <span className="ml-auto text-xs text-foreground-400">{cat.entryCount}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}